/** An inline divider rendered in the SMS thread, discriminated by `kind`. */
export type AgentDisabledItem = { kind: 'agent-disabled'; id: string; createdDate: string }
export type AppointmentItem = {
  kind: 'appointment'
  id: string
  createdDate: string
  date?: string | null
  time?: string | null
  type?: string | null
}
export type ThreadDividerItem = AgentDisabledItem | AppointmentItem

/** Minimal Event shape the agent-disabled builder reads. `data` is a JSON change set. */
export type ThreadEventForDivider = {
  id: string
  createdDate: string
  data?: unknown
}

/**
 * Build "Agent disabled" divider items from this thread's update Events. Thread
 * changes flow into the Event stream as field diffs; a transition to disabled
 * reads as data.aiEnabled.to === "false" (primitive leaves are stringified).
 */
export function agentDisabledDividers(events: ThreadEventForDivider[]): AgentDisabledItem[] {
  return events
    .filter((e) => {
      const change = (e.data as Record<string, { to?: unknown }> | null | undefined)?.aiEnabled
      // Event diffs arrive stringified: `{ to: "false" }`, not `{ to: false }`.
      return change?.to === `false` || change?.to === false
    })
    .map((e) => ({ kind: `agent-disabled` as const, id: e.id, createdDate: e.createdDate }))
}

/** Minimal appointment shape the milestone builder reads. */
export type AppointmentForMilestone = {
  id: string
  date?: string | null
  time?: string | null
  type?: string | null
  appointmentMadeDate?: string | null
}

/**
 * Build "Appointment booked" milestone items for the thread: every appointment
 * booked at or after `firstMessageDate` (i.e. while the conversation was
 * happening), anchored later by its `appointmentMadeDate`. Source-agnostic — the
 * caller passes the contact's appointments regardless of who booked them.
 */
export function appointmentMilestones(
  appointments: AppointmentForMilestone[],
  firstMessageDate: string | undefined,
): AppointmentItem[] {
  if (!firstMessageDate) return []
  const startMs = new Date(firstMessageDate).getTime()
  return appointments
    .filter((a) => !!a.appointmentMadeDate && new Date(a.appointmentMadeDate).getTime() >= startMs)
    .map((a) => ({
      kind: `appointment` as const,
      id: a.id,
      createdDate: a.appointmentMadeDate as string,
      date: a.date,
      time: a.time,
      type: a.type,
    }))
}

/**
 * Sort divider items ascending by `createdDate` so items that land before the
 * same message (or in the tail) render in chronological order. Returns a new
 * array; does not mutate the input.
 */
export function sortDividerItemsByDate(items: ThreadDividerItem[]): ThreadDividerItem[] {
  return [...items].sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime())
}

/**
 * Anchor each event before the first message whose timestamp is at or after the
 * event's `createdDate`. Events with no such message (i.e. after the last
 * message) go into `tail`. Mirrors the original inline logic in
 * useThreadDividers, generalized to carry the full event object.
 */
export function anchorThreadEvents<T extends { id: string; createdDate?: string | null }>(
  events: T[],
  sortedMessages: { id?: string | null; createdDate?: string | null }[],
): { beforeId: Map<string, T[]>; tail: T[] } {
  const beforeId = new Map<string, T[]>()
  const tail: T[] = []
  for (const e of events) {
    const eventMs = new Date(e.createdDate ?? ``).getTime()
    const nextMsg = sortedMessages.find((m) => !!m.createdDate && new Date(m.createdDate).getTime() >= eventMs)
    if (nextMsg?.id) {
      const list = beforeId.get(nextMsg.id) ?? []
      list.push(e)
      beforeId.set(nextMsg.id, list)
    } else {
      tail.push(e)
    }
  }
  return { beforeId, tail }
}
